import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const remembered = localStorage.getItem("cad_remember_user") || "";
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(!!remembered);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) nav("/", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (remember) localStorage.setItem("cad_remember_user", email); else localStorage.removeItem("cad_remember_user");
      await login(email.trim(), password);
      nav("/", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Dispatcher and administrator access.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <label className="block">
          <span className="overline-label">Username / Email</span>
          <input data-testid="login-email-input" className="field-input mt-1 h-9" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus required />
        </label>
        <label className="block">
          <span className="overline-label">Password</span>
          <div className="relative mt-1">
            <input data-testid="login-password-input" className="field-input h-9 pr-9" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
            <button type="button" data-testid="login-show-password-toggle" onClick={() => setShow((s) => !s)} className="absolute right-2 top-2 text-muted-foreground hover:text-foreground" aria-label="Show password">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </label>
        <div className="flex items-center justify-between text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="login-remember-checkbox" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#2563EB]" />
            <span>Remember username</span>
          </label>
          <Link data-testid="login-forgot-link" to="/forgot-password" className="text-primary hover:underline">Forgot password?</Link>
        </div>
        {error && <div data-testid="login-error" className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-300">{error}</div>}
        <Button data-testid="login-submit-button" type="submit" className="w-full h-9" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
    </AuthShell>
  );
}
